import { Fragment } from 'react';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { SWITZERLAND, type CountryOption } from './countries';

const BILLING_COUNTRIES = [SWITZERLAND] as const;

export type OrganizationDetailsFormState = {
  organizationName: string;
  billingAddressStreet: string;
  billingAddressStreetNumber: string;
  billingAddressPostalCode: string;
  billingAddressCountry: string;
};

export type OrganizationDetailsFormErrors = Partial<Record<keyof OrganizationDetailsFormState, string>>;

type OrganizationDetailsFormProps = {
  form: OrganizationDetailsFormState;
  errors: OrganizationDetailsFormErrors;
  onFieldChange: (name: keyof OrganizationDetailsFormState, value: string) => void;
  showOwnerHint?: boolean;
  idPrefix?: string;
};

export function createOrganizationDetailsInitialState(): OrganizationDetailsFormState {
  return {
    organizationName: '',
    billingAddressStreet: '',
    billingAddressStreetNumber: '',
    billingAddressPostalCode: '',
    billingAddressCountry: SWITZERLAND.iso2,
  };
}

export function validateOrganizationDetailsForm(form: OrganizationDetailsFormState) {
  const nextErrors: OrganizationDetailsFormErrors = {};

  if (!form.organizationName.trim()) {
    nextErrors.organizationName = 'Organization name is required';
  }

  if (!form.billingAddressStreet.trim()) {
    nextErrors.billingAddressStreet = 'Street is required';
  }

  if (!form.billingAddressStreetNumber.trim()) {
    nextErrors.billingAddressStreetNumber = 'Street number is required';
  }

  if (!form.billingAddressPostalCode.trim()) {
    nextErrors.billingAddressPostalCode = 'Postal code is required';
  }

  if (!form.billingAddressCountry.trim()) {
    nextErrors.billingAddressCountry = 'Billing country is required';
  }

  return nextErrors;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;

  return <span style={{ display: 'block', marginTop: '8px', color: '#dc2626', fontSize: '12px' }}>{message}</span>;
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        style={{
          width: '100%',
          height: '40px',
          padding: '0 14px',
          border: error ? '1px solid #dc2626' : '0',
          borderRadius: '6px',
          background: 'rgba(255, 255, 255, 0.35)',
          cursor: 'text',
        }}
      />
      <FieldError message={error} />
    </div>
  );
}

function SelectField({
  id,
  label,
  value,
  onChange,
  error,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  options: readonly CountryOption[];
}) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          width: '100%',
          height: '40px',
          padding: '0 14px',
          border: error ? '1px solid #dc2626' : '0',
          borderRadius: '6px',
          background: 'rgba(255, 255, 255, 0.35)',
          appearance: 'auto',
          cursor: 'pointer',
        }}
      >
        {options.map((option) => (
          <option key={option.iso2} value={option.iso2}>
            {option.label}
          </option>
        ))}
      </select>
      <FieldError message={error} />
    </div>
  );
}

export function OrganizationDetailsForm({
  form,
  errors,
  onFieldChange,
  showOwnerHint = true,
  idPrefix = 'organization-details',
}: OrganizationDetailsFormProps) {
  return (
    <Fragment>
      {showOwnerHint && (
        <p>
          Please insert your organization name. You will be the administrator of this organization since you are
          creating it.
          <CustomTooltip title="As an owner you will have to handle the subscription plan renewal. You will be able to invite users, assign them roles, and grant visibility over the organization's projects. You will also be able to delete the organization." />
        </p>
      )}

      <div
        style={{
          marginTop: showOwnerHint ? '18px' : '0',
          marginBottom: '14px',
          width: 'calc((100% - 28px) / 2)',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: '14px',
          alignItems: 'start',
        }}
      >
        <TextField
          id={`${idPrefix}-organization-name`}
          label="Organization name"
          value={form.organizationName}
          onChange={(value) => onFieldChange('organizationName', value)}
          error={errors.organizationName}
        />
      </div>

      <div style={{ marginBottom: '18px' }}>
        <h3
          style={{
            marginBottom: '12px',
            fontWeight: 700,
            fontSize: '22px',
            lineHeight: 1.2,
          }}
        >
          Billing address
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 1.6fr) minmax(140px, 0.8fr) minmax(140px, 0.8fr)',
            gap: '14px',
          }}
        >
          <TextField
            id={`${idPrefix}-billing-street`}
            label="Street"
            value={form.billingAddressStreet}
            onChange={(value) => onFieldChange('billingAddressStreet', value)}
            error={errors.billingAddressStreet}
          />
          <TextField
            id={`${idPrefix}-billing-street-number`}
            label="Street number"
            value={form.billingAddressStreetNumber}
            onChange={(value) => onFieldChange('billingAddressStreetNumber', value)}
            error={errors.billingAddressStreetNumber}
          />
          <TextField
            id={`${idPrefix}-billing-postal-code`}
            label="Postal code"
            value={form.billingAddressPostalCode}
            onChange={(value) => onFieldChange('billingAddressPostalCode', value)}
            error={errors.billingAddressPostalCode}
          />
          <SelectField
            id={`${idPrefix}-billing-country`}
            label="Country"
            value={form.billingAddressCountry}
            onChange={(value) => onFieldChange('billingAddressCountry', value)}
            error={errors.billingAddressCountry}
            options={BILLING_COUNTRIES}
          />
        </div>
      </div>
    </Fragment>
  );
}
